/* Sign in by a code: the artboard's `authPhone`, `authEmail` and `authOtp`.
 *
 * PHONE IS BUILT AND PARKED. SMS waits on Meta, so production's configuration
 * leaves the phone route off the first screen and nothing here can be reached
 * that claims a code was sent. The screen exists so the route returns with a
 * configuration change and no code.
 *
 * EMAIL SIGNS IN WITH A CODE, not the artboard's password. Nothing that takes
 * a password ships.
 *
 * THE SIX CELLS SIT OVER ONE REAL INPUT. The artboard draws an on-screen keypad;
 * it is not built, because it would hide the phone's own offer to fill the code
 * from the message, which is faster than any keypad. The cells are what the
 * artboard draws; the input underneath takes typing, pasting and autofill.
 */
import { Diagnostics } from "@damga/ds";
import { Offline, RateLimited, type Session, t } from "@damga/kit";
import { useRef, useState } from "react";

import { API_URL, AUTH_URL } from "../config";
import { toE164Qatar } from "../phone";

type Auth = {
  requestCode(phone: string): Promise<void>;
  verifyCode(phone: string, code: string): Promise<Session>;
  requestEmailCode(email: string): Promise<void>;
  verifyEmailCode(email: string, code: string): Promise<Session>;
};

export type SignInMode = "phone" | "email";

const LOOKS_LIKE_EMAIL = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

export function SignIn({
  auth,
  mode = "phone",
  onSignedIn,
  onBack,
}: {
  auth: Auth;
  mode?: SignInMode;
  onSignedIn: (s: Session) => void;
  onBack: () => void;
}) {
  const email = mode === "email";
  const [step, setStep] = useState<"identifier" | "code">("identifier");
  const [value, setValue] = useState("");
  const [target, setTarget] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeInput = useRef<HTMLInputElement>(null);

  async function send() {
    const normalised = email
      ? LOOKS_LIKE_EMAIL.test(value.trim())
        ? value.trim()
        : null
      : toE164Qatar(value);
    if (!normalised) {
      setError(email ? t("auth.badEmail") : t("auth.badNumber"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (email) await auth.requestEmailCode(normalised);
      else await auth.requestCode(normalised);
      setTarget(normalised);
      setCode("");
      setStep("code");
    } catch (err) {
      setError(
        err instanceof RateLimited
          ? t("auth.tooManyCodes")
          : err instanceof Offline
            ? t("auth.unreachable")
            : t("error.generic.body"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      onSignedIn(email ? await auth.verifyEmailCode(target, code) : await auth.verifyCode(target, code));
    } catch (err) {
      setError(err instanceof Offline ? t("auth.unreachable") : t("auth.badCode"));
    } finally {
      setBusy(false);
    }
  }

  if (step === "code") {
    return (
      <main className="auth">
        <button type="button" className="back" onClick={() => setStep("identifier")}>
          ← {email ? t("auth.changeEmail") : t("auth.changeNumber")}
        </button>
        <div>
          <h1 className="display">{t("auth.otpTitle")}</h1>
          <p className="lead">{target}</p>
        </div>

        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <label className="otp">
            <span className="visually-hidden">{t("auth.otpTitle")}</span>
            {Array.from({ length: 6 }, (_, i) => (
              <span
                key={i}
                className="otp-cell"
                aria-hidden="true"
                data-filled={code[i] ? "" : undefined}
                data-next={i === code.length ? "" : undefined}
              >
                {code[i] ?? ""}
              </span>
            ))}
            <input
              ref={codeInput}
              className="otp-input"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                setError(null);
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              aria-invalid={error ? true : undefined}
              autoFocus
            />
          </label>

          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="btn btn-primary" disabled={busy || code.length !== 6}>
            {busy ? t("common.loading") : t("auth.verify")}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="auth">
      <button type="button" className="back" onClick={onBack}>
        ← {t("auth.allOptions")}
      </button>
      <div>
        <h1 className="display">{email ? t("auth.emailTitle") : t("auth.title")}</h1>
        <p className="lead">{email ? t("auth.emailBody") : t("auth.body")}</p>
      </div>

      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <label className="field">
          <span className="field-label">{email ? t("auth.email") : t("auth.mobile")}</span>
          {email ? (
            <input
              className="input"
              type="email"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={error ? true : undefined}
              autoFocus
            />
          ) : (
            <span className="input-group">
              <span className="input-prefix">+974</span>
              <input
                className="input input-mono"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="5551 2345"
                aria-invalid={error ? true : undefined}
                autoFocus
              />
            </span>
          )}
        </label>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn btn-primary" disabled={busy || value.trim().length < 6}>
          {busy ? t("common.loading") : t("auth.send")}
        </button>
      </form>

      <p className="fine">{email ? t("auth.emailConsent") : t("auth.consent")}</p>

      {/* A support tool, not a feature: it prints internal addresses, so it is
          only mounted outside production. */}
      {!import.meta.env.PROD && <Diagnostics apiUrl={API_URL} authUrl={AUTH_URL} />}
    </main>
  );
}
